import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  adminCreateArticleFn,
  adminPublishArticleFn,
} from "~/lib/functions/cih/articles";
import { publishedChallengesQueryOptions } from "~/lib/functions/cih/challenges";
import { generateSlug } from "~/lib/shared/slug";

export const Route = createFileRoute("/admin/cih/articles/new")({
  staticData: { title: "New Article" },
  component: NewAdminArticlePage,
});

function NewAdminArticlePage() {
  const navigate = useNavigate();
  const { data: challenges = [] } = useQuery(publishedChallengesQueryOptions);

  const form = useForm({
    defaultValues: {
      authorName: "",
      authorTitle: "",
      challengeId: "",
      title: "",
      description: "",
      body: "",
    },
  });

  const values = useStore(form.store, (state) => state.values);

  const isValid =
    values.authorName.trim().length > 0 &&
    values.challengeId.length > 0 &&
    values.title.trim().length > 0 &&
    values.description.trim().length > 0;

  const createMutation = useMutation({
    mutationFn: async ({ publish }: { publish: boolean }) => {
      let body: unknown;
      try {
        body = values.body ? JSON.parse(values.body) : undefined;
      } catch {
        body = undefined;
      }
      const row = await adminCreateArticleFn({
        data: {
          slug: generateSlug(values.title),
          title: values.title,
          description: values.description,
          challengeId: values.challengeId,
          authorName: values.authorName.trim(),
          authorTitle: values.authorTitle.trim() || undefined,
          body,
        },
      });
      if (publish) {
        await adminPublishArticleFn({ data: { id: row.id } });
      }
      return row;
    },
    onSuccess: (row, { publish }) => {
      toast.success(publish ? "Article published" : "Draft saved");
      void navigate({
        to: "/admin/cih/articles/$articleId",
        params: { articleId: row.id },
      });
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Failed to save article",
      );
    },
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-medium text-gray-900">
            New Article
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Editorial article with a free-text author (not linked to a coach).
          </p>
        </div>

        <Card>
          <CardContent className="space-y-5 pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="admin-article-author-name" className="mb-1">
                  Author name
                </Label>
                <form.Field name="authorName">
                  {(field) => (
                    <Input
                      id="admin-article-author-name"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="e.g. Dream Coach Match Editorial"
                    />
                  )}
                </form.Field>
              </div>
              <div>
                <Label htmlFor="admin-article-author-title" className="mb-1">
                  Author title (optional)
                </Label>
                <form.Field name="authorTitle">
                  {(field) => (
                    <Input
                      id="admin-article-author-title"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="e.g. Editorial Team"
                    />
                  )}
                </form.Field>
              </div>
            </div>

            <div>
              <Label htmlFor="admin-article-challenge" className="mb-1">
                Challenge
              </Label>
              <form.Field name="challengeId">
                {(field) => (
                  <Select
                    value={field.state.value}
                    onValueChange={field.handleChange}
                  >
                    <SelectTrigger
                      id="admin-article-challenge"
                      className="w-full"
                    >
                      <SelectValue placeholder="Select a challenge..." />
                    </SelectTrigger>
                    <SelectContent>
                      {challenges.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </form.Field>
            </div>

            <div>
              <Label htmlFor="admin-article-title" className="mb-1">
                Title
              </Label>
              <form.Field name="title">
                {(field) => (
                  <Input
                    id="admin-article-title"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="Article title"
                  />
                )}
              </form.Field>
            </div>

            <div>
              <Label htmlFor="admin-article-description" className="mb-1">
                Description
              </Label>
              <form.Field name="description">
                {(field) => (
                  <Textarea
                    id="admin-article-description"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="A short summary of the article"
                    rows={3}
                    maxLength={300}
                    className="min-h-20 resize-y"
                  />
                )}
              </form.Field>
            </div>

            <div>
              <Label htmlFor="admin-article-body" className="mb-1">
                Body
              </Label>
              <form.Field name="body">
                {(field) => (
                  <RichTextEditor
                    content={field.state.value}
                    onChange={field.handleChange}
                    placeholder="Write the article..."
                    className="min-h-[300px]"
                  />
                )}
              </form.Field>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button
            type="button"
            variant="default"
            disabled={!isValid || createMutation.isPending}
            onClick={() => createMutation.mutate({ publish: false })}
          >
            {createMutation.isPending ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!isValid || createMutation.isPending}
            onClick={() => createMutation.mutate({ publish: true })}
          >
            {createMutation.isPending ? "Saving..." : "Save & Publish"}
          </Button>
        </div>
      </div>
    </div>
  );
}
